import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Tabs, Form, Input, Button, message, Row, Col, Select, Spin } from 'antd';
import { UserOutlined, CheckCircleOutlined, LeftOutlined, RightOutlined, QuestionCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import GeneralService from "../../../../services/Producto_3/Fort_Capacidades/DHAA/cursoFortalecimientoDHAA";
import EscuestaAsistencia from "./EscuelaDHHAAsistencia"

const { Option } = Select;

const EscuelaDHAAForm = forwardRef(({ generalFormRef, preguntasFormRef, incidenciasFormRef, onFinish, user }, ref) => {
  const [currentTab, setCurrentTab] = useState(1);
  const [grupoEtnico, setGrupoEtnico] = useState([]);
  const [organizacion, setOrganizacion] = useState([]);
  const [grupoEtareo, setGrupoEtareo] = useState([]);
  const [vereda, setVereda] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [filterDepartamento, setFilterDepartamento] = useState(null);
  const [filteredMunicipios, setFilteredMunicipios] = useState([]);
  const [filterMunicipio, setFilterMunicipio] = useState(null);
  const [filterVereda, setFilterVereda] = useState(null);
  const [filteredVeredas, setFilteredVeredas] = useState([]);
  const [oidData, setOidData] = useState(null);
  const [incidencias, setIncidencias] = useState([]);
  const [accionesSociales, setAccionesSociales] = useState([]);
  const [problematicas, setProblematicas] = useState([]);
  const [selectedIncidencia, setSelectedIncidencia] = useState(null);
  const [selectedSocial, setSelectedSocial] = useState(null);
  const [selectedProblematica, setSelectedProblematica] = useState(null);
  const [dataUser, setDataUser] = useState(null);
  const [dataPreguntas, setDataPreguntas] = useState(null);
  const [dataIncidencias, setDataIncidencias] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Crear refs internos como respaldo
  const internalGeneralFormRef = React.useRef(null);
  const internalPreguntasFormRef = React.useRef(null);
  const internalIncidenciasFormRef = React.useRef(null);

  // Usar los refs externos si están disponibles, de lo contrario usar los internos
  const actualGeneralFormRef = generalFormRef || internalGeneralFormRef;
  const actualPreguntasFormRef = preguntasFormRef || internalPreguntasFormRef;
  const actualIncidenciasFormRef = incidenciasFormRef || internalIncidenciasFormRef;

  // Resetear el formulario cuando el usuario cambia
  const resetForm = useCallback(() => {
    actualGeneralFormRef.current?.resetFields();
    actualPreguntasFormRef.current?.resetFields();
    actualIncidenciasFormRef.current?.resetFields();
    setCurrentTab(1);
    setDataUser(null);
    setDataPreguntas(null);
    setDataIncidencias(null);
    setOidData(null);
    setFilterDepartamento(null);
    setFilterMunicipio(null);
    setFilterVereda(null);
    setFilteredMunicipios([]);
    setFilteredVeredas([]);
  }, [actualGeneralFormRef, actualPreguntasFormRef, actualIncidenciasFormRef]);

  // Exponer métodos al componente padre
  useImperativeHandle(ref, () => ({
    resetFields: resetForm
  }));

  // Cargar datos iniciales (solo una vez)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [
          grupoEtareoData,
          departamentosData,
          municipiosData,
          veredaData,
          grupoEtnicoData,
          organizacionData,
          incidenciaData,
          socialData,
          problematicaData,
        ] = await Promise.all([
          GeneralService.getAllGrupoEtareo(),
          GeneralService.getDepartamentos(),
          GeneralService.getMunicipios(),
          GeneralService.getAllVereda(),
          GeneralService.getAllGrupoEtnico(),
          GeneralService.getOrganizacion(),
          GeneralService.getAllIncidencia(),
          GeneralService.getAllSocial(),
          GeneralService.getAllProblematica(),
        ]);

        setGrupoEtareo(grupoEtareoData);
        setDepartamentos(departamentosData);
        setMunicipios(municipiosData);
        setVereda(veredaData);
        setGrupoEtnico(grupoEtnicoData);
        setOrganizacion(organizacionData);
        setIncidencias(incidenciaData);
        setAccionesSociales(socialData);
        setProblematicas(problematicaData);

        setInitialDataLoaded(true);
      } catch (error) {
        console.error('Error al cargar los datos:', error);
        message.error('Error al cargar los datos iniciales');
      } finally {
        setLoading(false);
      }
    };

    if (!initialDataLoaded) {
      fetchInitialData();
    }
  }, [initialDataLoaded]);

  // Cargar datos del usuario cuando cambia el prop 'user'
  useEffect(() => {
    if (user && initialDataLoaded) {
      loadUserData();
    } else if (!user) {
      // Si no hay user, es modo creación - resetear formularios
      resetForm();
    }
  }, [user, initialDataLoaded, resetForm]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const usuarioData = await GeneralService.getGeneralById(user);
      setDataUser(usuarioData);
      setOidData(user);

      // Cargar datos de preguntas e incidencias en paralelo
      const [preguntaData, incidenciaData] = await Promise.all([
        GeneralService.getAllPreguntas(user).catch(error => {
          console.warn('Error cargando preguntas:', error);
          return null;
        }),
        GeneralService.getAllIncidenciaGeneral(user).catch(error => {
          console.warn('Error cargando incidencias:', error);
          return null;
        })
      ]);

      setDataPreguntas(preguntaData);
      setDataIncidencias(incidenciaData);

      // Pequeño delay para asegurar que los formularios estén montados
      setTimeout(() => {
        // Establecer valores en el formulario general
        if (actualGeneralFormRef.current) {
          const municipioObj = municipios.find(m => m.oid === usuarioData.municipio);
          const departamentoObj = municipioObj ? departamentos.find(d => d.oid === municipioObj.oid_depto) : null;
          const veredaObj = vereda.find(v => v.oid === usuarioData.vereda);

          actualGeneralFormRef.current.setFieldsValue({
            nombre_apellido: usuarioData.nombre_apellido || '',
            tipo_documento: usuarioData.tipo_documento ? String(usuarioData.tipo_documento) : undefined,
            numero_documento: usuarioData.numero_documento || '',
            correo: usuarioData.correo || '',
            telefono: usuarioData.telefono || '',
            sexo: usuarioData.sexo ? String(usuarioData.sexo) : undefined,
            edad: usuarioData.edad || '',
            grupo_etareo: usuarioData.grupo_etareo || undefined,
            municipio: usuarioData.municipio || undefined,
            departamento: departamentoObj ? departamentoObj.oid : undefined,
            vereda: usuarioData.vereda || undefined,
            pertenencia_etnica: usuarioData.pertenencia_etnica || undefined,
            organizacion: usuarioData.organizacion || undefined,
          });

          // Actualizar filtros basados en los datos del usuario
          if (departamentoObj) {
            handleDepartamentoChange(departamentoObj.oid);
          }
          if (municipioObj) {
            handleMunicipioChange(municipioObj.oid);
          }
        }

        // Establecer valores en el formulario de preguntas
        if (actualPreguntasFormRef.current && preguntaData) {
          actualPreguntasFormRef.current.setFieldsValue({
            pregunta_1: preguntaData.pregunta_1 ?? undefined,
            pregunta_2: preguntaData.pregunta_2 ?? undefined,
            pregunta_3: preguntaData.pregunta_3 ?? undefined,
            pregunta_4: preguntaData.pregunta_4 ?? undefined,
            pregunta_5: preguntaData.pregunta_5 ?? undefined,
            pregunta_6: preguntaData.pregunta_6 ?? '',
            pregunta_7: preguntaData.pregunta_7 ?? undefined,
          });
        }

        // Establecer valores en el formulario de incidencias
        if (actualIncidenciasFormRef.current && incidenciaData) {
          actualIncidenciasFormRef.current.setFieldsValue({
            accion_incidencia: incidenciaData.accion_incidencia || undefined,
            accion_social: incidenciaData.accion_social || undefined,
            accion_problematica: incidenciaData.accion_problematica || undefined,
          });
        }
      }, 50);
    } catch (error) {
      console.error('Error al cargar los datos del usuario:', error);
      message.error('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onFinish();
    resetForm();
  };

  const handleSubmit = async (section, values) => {
    try {
      let response;

      switch (section) {
        case 'general':
          if (dataUser?.oid) {
            const municipioID = typeof values.municipio === 'number' ?
              values.municipio :
              municipios.find(m => m.municipio === values.municipio)?.oid;

            const veredaID = typeof values.vereda === 'number' ?
              values.vereda :
              vereda.find(v => v.vereda === values.vereda)?.oid;

            const updatedValues = {
              ...values,
              municipio: municipioID,
              vereda: veredaID
            };

            response = await GeneralService.updateGeneral(dataUser.oid, updatedValues);
          } else {
            response = await GeneralService.createGeneral(values);
            if (response.oid) {
              setOidData(response.oid);
            }
          }
          break;

        case 'preguntas':
          if (dataPreguntas?.oid) {
            response = await GeneralService.updatePregunta(dataPreguntas.oid, values);
          } else {
            response = await GeneralService.crearPregunta({ ...values, general: oidData });
          }
          break;

        case 'incidencias':
          if (dataIncidencias?.oid) {
            response = await GeneralService.updateIncidencia(dataIncidencias.oid, values);
          } else {
            response = await GeneralService.crearIncidencia({ ...values, general: oidData });
          }
          break;

        default:
          message.error('Sección desconocida al guardar datos');
          return;
      }

      if (response?.oid) {
        message.success('Datos guardados exitosamente');
        handleNext();
      } else {
        message.error('Hubo un problema al guardar los datos');
      }
    } catch (error) {
      console.error(`Error al guardar datos de ${section}:`, error);
      message.error(`Error al guardar los datos: ${error.message}`);
    }
  };

  const handleNext = async () => {
    try {
      if (currentTab === 1) {
        await actualGeneralFormRef.current.validateFields();
      } else if (currentTab === 2) {
        await actualPreguntasFormRef.current.validateFields();
      } else if (currentTab === 3) {
        await actualIncidenciasFormRef.current.validateFields();
      }
      setCurrentTab(prev => prev + 1);
    } catch (error) {
    }
  };

  const handleBack = () => {
    setCurrentTab(prev => Math.max(1, prev - 1));
  };

  const handleTabChange = async (key) => {
    const newTab = Number(key);

    if (newTab > currentTab) {
      try {
        if (currentTab === 1) {
          await actualGeneralFormRef.current.validateFields();
        } else if (currentTab === 2) {
          await actualPreguntasFormRef.current.validateFields();
        } else if (currentTab === 3) {
          await actualIncidenciasFormRef.current.validateFields();
        }
        setCurrentTab(newTab);
      } catch (error) {
        return;
      }
    }
    setCurrentTab(newTab);
  };

  const handleDepartamentoChange = (value) => {
    setFilterDepartamento(value);
    const filtered = municipios.filter(municipio => municipio.oid_depto === value);
    setFilteredMunicipios(filtered);
    setFilterMunicipio(null);
    setFilterVereda(null);
    setFilteredVeredas([]);
  };

  const handleMunicipioChange = (value) => {
    setFilterMunicipio(value);
    const filtered = vereda.filter(v => v.oid_municipio === value);
    setFilteredVeredas(filtered);
    setFilterVereda(null);
  };

  if (loading && !initialDataLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const tabsItems = [
    {
      key: '1',
      label: (
        <span>
          <UserOutlined /> Datos Generales
        </span>
      ),
      children: (
        <Form
          ref={actualGeneralFormRef}
          onFinish={(values) => handleSubmit('general', values)}
          layout="vertical"
          initialValues={dataUser || {}}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="nombre_apellido" label="Nombre y Apellido" rules={[{ required: true }]}>
                <Input placeholder="Diligenciar nombre y apellido" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tipo_documento" label="Tipo de Documento" rules={[{ required: true }]}>
                <Select placeholder="Selecciona el tipo de documento">
                  <Select.Option value="1">Cédula de Ciudadanía</Select.Option>
                  <Select.Option value="2">Tarjeta de Identidad</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="numero_documento" label="Número de Documento" rules={[{ required: true }]}>
                <Input placeholder="Diligenciar número de documento" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="correo"
                label="Correo"
              >
                <Input placeholder="Diligenciar el correo" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="telefono" label="Teléfono" rules={[{ required: true }]}>
                <Input placeholder="Diligenciar el teléfono " />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sexo" label="Sexo" rules={[{ required: true }]}>
                <Select placeholder="Selecciona el genero">
                  <Select.Option value="1">Mujer</Select.Option>
                  <Select.Option value="2">Hombre</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="edad" label="Edad" rules={[{ required: true }]}>
                <Input placeholder="Diligenciar la edad" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="grupo_etareo"
                label="Grupo Etáreo"
                rules={[{ required: true, message: "Por favor selecciona un grupo etáreo" }]}
              >
                <Select placeholder="Selecciona un grupo etáreo">
                  {grupoEtareo.map((grupo) => (
                    <Option key={grupo.oid} value={grupo.oid}>
                      {grupo.etareo}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="departamento"
                label="Departamento"
                rules={[{ required: true, message: "Por favor selecciona un departamento" }]}
              >
                <Select
                  showSearch
                  placeholder="Filtrar por Departamento"
                  value={filterDepartamento || undefined}
                  onChange={handleDepartamentoChange}
                  onSearch={(value) => setFilterDepartamento(value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {departamentos.map(depto => (
                    <Option key={depto.oid} value={depto.oid}>{depto.departamento}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="municipio"
                label="Municipio"
                rules={[{ required: true, message: "Por favor selecciona un municipio" }]}
              >
                <Select
                  showSearch
                  placeholder="Filtrar por Municipio"
                  value={filterMunicipio || undefined}
                  onChange={handleMunicipioChange}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {filteredMunicipios.map(mun => (
                    <Option key={mun.oid} value={mun.oid}>{mun.municipio}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="vereda"
                label="Vereda"
                rules={[{ required: true, message: "Por favor selecciona una vereda" }]}
              >
                <Select
                  showSearch
                  placeholder="Filtrar por Vereda"
                  value={filterVereda || undefined}
                  onChange={(value) => setFilterVereda(value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {filteredVeredas.map(ver => (
                    <Option key={ver.oid} value={ver.oid}>{ver.vereda}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="pertenencia_etnica"
                label="Pertenencia Étnica"
                rules={[{ required: true, message: "Por favor selecciona una pertenencia étnica" }]}
              >
                <Select
                  showSearch
                  placeholder="Selecciona una Pertenencia Étnica"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {grupoEtnico.map(etnia => (
                    <Option key={etnia.oid} value={etnia.oid}>{etnia.etnico}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="organizacion"
                label="Organización"
                rules={[{ required: true, message: "Por favor selecciona una organización" }]}
              >
                <Select
                  showSearch
                  placeholder="Selecciona una Organización"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {organizacion.map(org => (
                    <Option key={org.oid} value={org.oid}>{org.organizacion}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
            {currentTab > 1 && (
              <Button onClick={handleBack} type="default" icon={<LeftOutlined />}>
                Atrás
              </Button>
            )}
            {currentTab < 4 && (
              <Button onClick={() => actualGeneralFormRef.current?.submit()} type="default" icon={<RightOutlined />}>
                Siguiente
              </Button>
            )}
          </div>
        </Form>
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <QuestionCircleOutlined /> Preguntas
        </span>
      ),
      children: (
        <Form
          ref={actualPreguntasFormRef}
          onFinish={(values) => handleSubmit('preguntas', values)}
          layout="vertical"
          initialValues={dataPreguntas || {}}
        >
          <Form.Item
            name="pregunta_1"
            label="¿Se considera campesino o campesina?"
            rules={[{ required: true, message: 'Por favor, indique si se considera campesino o campesina.' }]}
          >
            <Select placeholder="Seleccione">
              <Option value={1}>Sí</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="pregunta_2"
            label="¿Se encuentra en condición de discapacidad?"
            rules={[{ required: true, message: 'Por favor, indique si se encuentra en condición de discapacidad.' }]}
          >
            <Select placeholder="Seleccione">
              <Option value={1}>Sí</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="pregunta_3"
            label="¿Está reconocido como víctima del conflicto armado?"
            rules={[{ required: true, message: 'Por favor, indique si está reconocido como víctima del conflicto armado.' }]}
          >
            <Select placeholder="Seleccione">
              <Option value={1}>Sí</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="pregunta_4"
            label="¿Actualmente está vinculado o vinculada al proyecto?"
            rules={[{ required: true, message: 'Por favor, indique si está vinculado o vinculada al proyecto.' }]}
          >
            <Select placeholder="Seleccione">
              <Option value={1}>Sí</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="pregunta_5"
            label="¿Requiere apoyo económico para desplazarse al punto de encuentro?"
            rules={[{ required: true, message: 'Por favor, indique si requiere apoyo económico para el desplazamiento.' }]}
          >
            <Select placeholder="Seleccione">
              <Option value={1}>Sí</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="pregunta_7"
            label="¿Cuenta con dispositivos electrónicos como celular, computador o tablet?"
            rules={[{ required: true, message: 'Por favor, indique si cuenta con dispositivos electrónicos.' }]}
          >
            <Select placeholder="Seleccione">
              <Option value={1}>Sí</Option>
              <Option value={0}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="pregunta_6"
            label="¿Cuál es el costo promedio de transporte desde su residencia hasta el lugar de encuentro de la escuela?"
            rules={[{ required: true, message: 'Por favor, indique el costo promedio de transporte.' }]}
          >
            <Input placeholder="Ingrese el valor en pesos colombianos (COP)" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: 20 }}>
            {currentTab > 1 && (
              <Button onClick={handleBack} type="default" icon={<LeftOutlined />}>
                Atrás
              </Button>
            )}
            {currentTab < 4 && (
              <Button onClick={() => actualPreguntasFormRef.current?.submit()} type="default" icon={<RightOutlined />}>
                Siguiente
              </Button>
            )}
          </div>
        </Form>
      ),
    },
    {
      key: '3',
      label: (
        <span>
          <ExclamationCircleOutlined /> Incidencias
        </span>
      ),
      children: (
        <Form
          ref={actualIncidenciasFormRef}
          onFinish={(values) => handleSubmit('incidencias', values)}
          layout="vertical"
          initialValues={dataIncidencias || {}}
        >
          <Row justify="center" style={{ marginTop: 20 }}>
            <Col span={24}>
              <Form.Item name="accion_incidencia" label="Acción Incidencia" rules={[{ required: true }]}>
                <Select placeholder="Seleccione una acción Incidencia" >
                  {incidencias.map(item => (
                    <Option key={item.oid} value={item.oid}>
                      {item.incidencia}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="accion_social" label="Acción Social" rules={[{ required: true }]}>
                <Select
                  placeholder="Seleccione una acción Social"
                  value={selectedSocial}
                  onChange={value => setSelectedSocial(value)}
                >
                  {accionesSociales.map(item => (
                    <Option key={item.oid} value={item.oid}>
                      {item.accion_social}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="accion_problematica" label="Acción Problemática" rules={[{ required: true }]}>
                <Select
                  placeholder="Acción Problemática"
                  value={selectedProblematica}
                  onChange={value => setSelectedProblematica(value)}
                >
                  {problematicas.map(item => (
                    <Option key={item.oid} value={item.oid}>
                      {item.accion_problematica}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: 20 }}>
            {currentTab > 1 && (
              <Button onClick={handleBack} type="default" icon={<LeftOutlined />}>
                Atrás
              </Button>
            )}
            {currentTab < 4 && (
              <Button onClick={() => actualIncidenciasFormRef.current?.submit()} type="default" icon={<RightOutlined />}>
                Siguiente
              </Button>
            )}
          </div>
        </Form>
      ),
    },
    {
      key: '4',
      label: (
        <span>
          <CheckCircleOutlined /> Asistencia
        </span>
      ),
      children: (
        <>
          <EscuestaAsistencia oidData={oidData} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: 20 }}>
            {currentTab > 1 && (
              <Button onClick={handleBack} type="default" icon={<LeftOutlined />}>
                Atrás
              </Button>
            )}
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleFinish}>
              Finalizar
            </Button>
          </div>
        </>
      ),
    }
  ];

  return (
    <div>
      <Tabs activeKey={String(currentTab)} onChange={handleTabChange} items={tabsItems} />
    </div>
  );
});

export default EscuelaDHAAForm;